import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getDeviceIcon(device) {
  if (device === 'mobile') return '📱';
  if (device === 'tablet') return '📲';
  return '💻';
}

function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="bg-white p-6 rounded-2xl shadow-lg" style={{borderTop: '4px solid #EBBEDA'}}>
        <div className="text-4xl mb-2">👁️</div>
        <div className="text-4xl font-bold" style={{color: '#EBBEDA'}}>{stats.total_views}</div>
        <div className="text-gray-600">Total Page Views</div>
      </div>
      <div className="bg-white p-6 rounded-2xl shadow-lg" style={{borderTop: '4px solid #EBBEDA'}}>
        <div className="text-4xl mb-2">👤</div>
        <div className="text-4xl font-bold" style={{color: '#EBBEDA'}}>{stats.unique_visitors}</div>
        <div className="text-gray-600">Unique Visitors</div>
      </div>
      <div className="bg-white p-6 rounded-2xl shadow-lg" style={{borderTop: '4px solid #EBBEDA'}}>
        <div className="text-4xl mb-2">📅</div>
        <div className="text-4xl font-bold" style={{color: '#EBBEDA'}}>{stats.period_days}</div>
        <div className="text-gray-600">Days Period</div>
      </div>
    </div>
  );
}

function VisitorsTable({ visitors }) {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
      <h2 className="text-xl font-bold text-gray-800 mb-4">🕐 Recent Visitors</h2>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{borderColor: '#EBBEDA'}}>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Time</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Page</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Location</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Device</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Referrer</th>
            </tr>
          </thead>
          <tbody>
            {visitors.map((visitor) => (
              <tr key={`${visitor.timestamp}-${visitor.page}`} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-2 text-sm text-gray-600">
                  {visitor.timestamp ? new Date(visitor.timestamp).toLocaleString() : '-'}
                </td>
                <td className="py-3 px-2 font-medium">{visitor.page || 'Home'}</td>
                <td className="py-3 px-2 text-sm">
                  {visitor.city !== 'Unknown' ? `${visitor.city}, ` : ''}{visitor.country}
                </td>
                <td className="py-3 px-2 text-sm capitalize">{visitor.device}</td>
                <td className="py-3 px-2 text-sm text-gray-500 truncate max-w-xs">
                  {visitor.referrer || '-'}
                </td>
              </tr>
            ))}
            {visitors.length === 0 && (
              <tr>
                <td colSpan="5" className="py-8 text-center text-gray-500">No visitors yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DEFAULT_DAYS = 30;
const HTTP_UNAUTHORIZED = 401;

function DailyViewsChart({ dailyViews }) {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-4">📈 Daily Views (Last 7 Days)</h2>
      <div className="space-y-3">
        {Object.entries(dailyViews).map(([date, views]) => {
          const maxViews = Math.max(...Object.values(dailyViews), 1);
          const percentage = (views / maxViews) * 100;
          return (
            <div key={date} className="flex items-center gap-3">
              <div className="w-24 text-sm text-gray-600">{date.slice(5)}</div>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{width: `${percentage}%`, background: '#EBBEDA'}} />
              </div>
              <div className="w-12 text-right font-medium">{views}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeviceBreakdown({ devices }) {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-4">📱 Views by Device</h2>
      <div className="space-y-4">
        {Object.entries(devices).map(([device, views]) => {
          const total = Object.values(devices).reduce((a, b) => a + b, 0) || 1;
          const percentage = ((views / total) * 100).toFixed(1);
          return (
            <div key={device} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getDeviceIcon(device)}</span>
                <span className="font-medium capitalize">{device}</span>
              </div>
              <div className="text-right">
                <div className="font-bold" style={{color: '#EBBEDA'}}>{views}</div>
                <div className="text-sm text-gray-500">{percentage}%</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RankedList({ title, icon, data, emptyText }) {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-4">{icon} {title}</h2>
      <div className="space-y-2">
        {Object.entries(data).slice(0, 10).map(([label, views], idx) => (
          <div key={label} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold text-white" style={{background: '#EBBEDA'}}>{idx + 1}</span>
              <span className="font-medium truncate max-w-xs">{label || 'Home'}</span>
            </div>
            <div className="font-bold" style={{color: '#EBBEDA'}}>{views}</div>
          </div>
        ))}
        {Object.keys(data).length === 0 && (
          <p className="text-gray-500 text-center py-4">{emptyText}</p>
        )}
      </div>
    </div>
  );
}

function AnalyticsDashboard({ stats }) {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <StatsCards stats={stats} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <DailyViewsChart dailyViews={stats.daily_views} />
        <DeviceBreakdown devices={stats.views_by_device} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <RankedList title="Top Countries" icon="🌍" data={stats.views_by_country} emptyText="No data yet" />
        <RankedList title="Top Pages" icon="📄" data={stats.views_by_page} emptyText="No data yet" />
      </div>
      <VisitorsTable visitors={stats.recent_visitors} />
      {Object.keys(stats.top_referrers).length > 0 && (
        <RankedList title="Top Referrers" icon="🔗" data={stats.top_referrers} emptyText="" />
      )}
    </div>
  );
}

function Analytics() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(DEFAULT_DAYS);

  const fetchStats = useCallback(async (pwd) => { // eslint-disable-line react-hooks/exhaustive-deps -- API_URL and HTTP_UNAUTHORIZED are module-level constants, setters are stable
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/analytics/stats?password=${encodeURIComponent(pwd)}&days=${days}`);
      if (response.status === HTTP_UNAUTHORIZED) {
        setError('Invalid password');
        setIsAuthenticated(false);
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      const data = await response.json();
      setStats(data);
      setIsAuthenticated(true);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Analytics fetch error:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchStats(password);
  };

  useEffect(() => {
    if (isAuthenticated && password) {
      fetchStats(password);
    }
  }, [days, isAuthenticated, password, fetchStats]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #FAF8FB 0%, #FFFFFF 50%, #F5F0F6 100%)'}}>
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4" style={{borderTop: '4px solid #EBBEDA'}}>
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            📊 <span style={{color: '#EBBEDA'}}>Analytics</span>
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2"
                style={{borderColor: '#EBBEDA', focusRingColor: '#EBBEDA'}}
                placeholder="Enter analytics password"
                data-testid="analytics-password"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              className="w-full px-6 py-3 text-white rounded-xl font-semibold transition-all hover:opacity-90"
              style={{background: '#EBBEDA'}}
              data-testid="analytics-login"
            >
              View Analytics
            </button>
          </form>
          <div className="mt-4 text-center">
            <a href="/" className="text-gray-500 hover:text-gray-700 text-sm">← Back to site</a>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      );
    }
    if (!stats) return null;
    return <AnalyticsDashboard stats={stats} />;
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #FAF8FB 0%, #FFFFFF 50%, #F5F0F6 100%)'}}>
      {/* Header */}
      <div className="bg-white shadow-sm" style={{borderBottom: '2px solid #EBBEDA'}}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">
            📊 <span style={{color: '#EBBEDA'}}>Analytics Dashboard</span>
          </h1>
          <div className="flex items-center gap-4">
            <select 
              value={days} 
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-4 py-2 rounded-xl border-2 focus:outline-none"
              style={{borderColor: '#EBBEDA'}}
              data-testid="days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <a href="/" className="text-gray-600 hover:text-gray-800">← Back</a>
          </div>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}

export default Analytics;
